import axios from 'axios';
import jwtDecode from 'jwt-decode';

export type ClientOptions = {
  httpUrl: string;
  getJWT: () => string | null;
  refreshJWT?: () => Promise<string | null>;
  clearJWT?: () => Promise<string | null>;
};

const isTokenExpired = (token: string) => {
  // exp is in seconds. Refresh within 30 seconds of expiring
  return jwtDecode<{ exp: number }>(token).exp <= Date.now() / 1000 + 5;
};

export const responseErrorHandler = async ({
  response,
  clearJWT,
  refreshJWT,
}: {
  response: any;
  clearJWT?: any;
  refreshJWT?: any;
}) => {
  const resp = response.response;
  let error = resp;

  if (!resp) {
    console.error('Network error'); // E.g. If backend was not up
  } else if ([401, 403].includes(resp.status)) {
    error = resp.data && resp.data.error;
    if (clearJWT) await clearJWT();
  } else if (resp.status === 400 && resp.data.error === 'invalid_grant' && refreshJWT) {
    console.error('Expired access token', resp.data);
    return refreshJWT();
    // return this.logout();
  } else {
    error = resp.data && resp.data.error;
  }

  return Promise.reject(error);
};

export const createClient = ({ httpUrl: url, getJWT, refreshJWT, clearJWT }: ClientOptions) => {
  const httpClient = axios.create({
    baseURL: url,
    transformRequest: [
      (data, headers) => {
        const token = getJWT();

        if (refreshJWT && token && isTokenExpired(token)) {
          console.log('token expired... refreshing');
          // TODO: can't do async function in this transformer; figure out alternative for token refresh
          // token = refreshJWT();
          console.log('token successfully refreshed');
        }
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        } else if (headers.Authorization) delete headers.Authorization;

        return JSON.stringify(data);
      },
    ],
  });

  httpClient.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8';

  httpClient.interceptors.response.use(
    response => {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      return response;
    },
    async errorResponse => {
      return responseErrorHandler({ response: errorResponse, refreshJWT, clearJWT });
    },
  );

  return { httpClient };
};
